import type { EmblaOptionsType } from 'embla-carousel';
import { classes } from 'html-classes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Icon from '~/components/Icon/Icon';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { IconButton, SIZE } from '~/components/isolated/IconButton';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';

import type { ReviewMediaLightboxProps } from '../../interfaces';
import { formatCustomerName, formatReviewDate, getMediaUrl } from '../../utils';

import StarRating from '../StarRating';

import styles from './ReviewMediaLightbox.module.scss';

const ReviewMediaLightbox = ({
  isOpen,
  onClose,
  allMediaWithReviews,
  initialIndex,
}: ReviewMediaLightboxProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [mainCarouselApi, setMainCarouselApi] = useState<CarouselApi>();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  const {
    onPrevButtonClick,
    onNextButtonClick,
    prevBtnDisabled,
    nextBtnDisabled,
  } = usePrevNextButtons(mainCarouselApi);

  const opts: EmblaOptionsType = {
    startIndex: initialIndex,
    loop: false,
    dragFree: false,
    containScroll: 'trimSnaps',
    align: 'center',
  };

  // Get current review based on selected media
  const currentItem = allMediaWithReviews[selectedIndex];
  const currentReview = currentItem?.review;

  // Get current review's media
  const currentReviewMedia = useMemo(() => {
    if (!currentReview?.media) {
      return [];
    }
    return currentReview.media;
  }, [currentReview]);

  // Find the index of selected media within current review's media
  const selectedMediaIndexInReview = useMemo(() => {
    if (!currentItem?.media || !currentReviewMedia.length) {
      return 0;
    }
    return currentReviewMedia.findIndex((m) => m.id === currentItem.media.id);
  }, [currentItem, currentReviewMedia]);

  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;

  const allMediaRef = useRef(allMediaWithReviews);
  allMediaRef.current = allMediaWithReviews;

  const onSelect = useCallback(() => {
    if (!mainCarouselApi) {
      return;
    }

    const newIndex = mainCarouselApi.selectedScrollSnap();

    // Pause previous video when switching slides
    const prevItem = allMediaRef.current[selectedIndexRef.current];
    if (prevItem?.media.mediaType === 'video') {
      const prevVideo = videoRefs.current.get(prevItem.media.id);
      if (prevVideo) {
        prevVideo.pause();
      }
    }

    setSelectedIndex(newIndex);
  }, [mainCarouselApi]);

  const handleVideoClick = useCallback((mediaId: string) => {
    const video = videoRefs.current.get(mediaId);
    if (video) {
      if (video.paused) {
        video.play();
        setPlayingVideoId(mediaId);
      } else {
        video.pause();
        setPlayingVideoId(null);
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isBottomSheetOpen) {
          setIsBottomSheetOpen(false);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        onPrevButtonClick();
      } else if (e.key === 'ArrowRight') {
        onNextButtonClick();
      }
    },
    [onClose, onPrevButtonClick, onNextButtonClick, isBottomSheetOpen],
  );

  // Handle clicking on a thumbnail from current review's media
  const handleThumbnailClick = useCallback(
    (mediaId: string) => {
      const globalIndex = allMediaWithReviews.findIndex(
        (item) => item.media.id === mediaId,
      );
      if (globalIndex !== -1 && mainCarouselApi) {
        mainCarouselApi.scrollTo(globalIndex);
        setSelectedIndex(globalIndex);
      }
    },
    [allMediaWithReviews, mainCarouselApi],
  );

  const handleOpenBottomSheet = useCallback(() => {
    setIsBottomSheetOpen(true);
  }, []);

  const handleCloseBottomSheet = useCallback(() => {
    setIsBottomSheetOpen(false);
  }, []);

  useEffect(() => {
    if (!mainCarouselApi) {
      return;
    }
    onSelect();
    mainCarouselApi.on('select', onSelect).on('reInit', onSelect);

    return () => {
      mainCarouselApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [mainCarouselApi, onSelect]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('body-no-scroll');
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.classList.remove('body-no-scroll');
    }

    return () => {
      document.body.classList.remove('body-no-scroll');
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Reset to initial index when opening
  useEffect(() => {
    if (isOpen && mainCarouselApi) {
      mainCarouselApi.scrollTo(initialIndex, true);
      setSelectedIndex(initialIndex);
      setIsBottomSheetOpen(false);
    }
  }, [isOpen, initialIndex, mainCarouselApi]);

  if (!isOpen || !currentReview) {
    return null;
  }

  const isCarouselActive = allMediaWithReviews.length > 1;
  const totalCount = allMediaWithReviews.length;

  // Truncate review text for preview
  const truncatedText =
    currentReview.content.length > 50
      ? `${currentReview.content.slice(0, 50)}...`
      : currentReview.content;

  // Mobile layout
  if (isMobile) {
    return createPortal(
      <div className={styles.mobileOverlay}>
        {/* Header */}
        <div className={styles.mobileHeader}>
          <button
            type="button"
            onClick={onClose}
            className={styles.mobileBackButton}
          >
            <Icon type="chevron-3.1" size={24} />
          </button>
          <span className={styles.mobileCounter}>
            {selectedIndex + 1}/{totalCount}
          </span>
        </div>

        {/* Main image area */}
        <div className={styles.mobileImageSection}>
          <Carousel
            opts={{ ...opts, active: isCarouselActive }}
            setApi={setMainCarouselApi}
            className={styles.mainCarousel}
          >
            <CarouselContent className={styles.mainCarouselContent}>
              {allMediaWithReviews.map(({ media }) => (
                <CarouselItem
                  key={media.id}
                  className={styles.mainCarouselItem}
                >
                  {media.mediaType === 'video' ? (
                    <div
                      className={styles.videoWrapper}
                      onClick={() => handleVideoClick(media.id)}
                    >
                      <video
                        ref={(el) => {
                          if (el) {
                            videoRefs.current.set(media.id, el);
                          }
                        }}
                        src={getMediaUrl(media)}
                        className={styles.video}
                        playsInline
                        preload="metadata"
                      />
                      {playingVideoId !== media.id && (
                        <div className={styles.videoPlayOverlay}>
                          <Icon type="play" size={48} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <img
                      src={getMediaUrl(media)}
                      alt=""
                      className={styles.mainImage}
                      loading="lazy"
                    />
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Bottom preview bar */}
        <div className={styles.mobileBottomBar}>
          <div className={styles.mobilePreviewContent}>
            <div className={styles.mobilePreviewTop}>
              <span className={styles.mobileAuthorName}>
                {formatCustomerName(currentReview.customer)}
              </span>
              <StarRating rating={currentReview.mark} size={14} />
              <button
                type="button"
                onClick={handleOpenBottomSheet}
                className={styles.mobileViewReviewBtn}
              >
                {t('reviewSeeReview')}
              </button>
            </div>
            <p className={styles.mobilePreviewText}>{truncatedText}</p>
          </div>
        </div>

        {/* Bottom sheet */}
        <div
          className={classes([
            styles.mobileBottomSheet,
            isBottomSheetOpen && styles.mobileBottomSheetOpen,
          ])}
        >
          {/* Backdrop */}
          {isBottomSheetOpen && (
            <div
              className={styles.mobileBottomSheetBackdrop}
              onClick={handleCloseBottomSheet}
            />
          )}

          {/* Sheet content */}
          <div className={styles.mobileBottomSheetContent}>
            {/* Header */}
            <div className={styles.mobileSheetHeader}>
              <span className={styles.mobileSheetTitle}>
                {t('reviewTitle')}
              </span>
              <IconButton
                icon="close"
                size={SIZE.M}
                variant="outline"
                onClick={handleCloseBottomSheet}
                className={styles.mobileSheetClose}
              />
            </div>

            {/* Author */}
            <div className={styles.mobileSheetAuthor}>
              <div className={styles.mobileSheetAuthorInfo}>
                <div className={styles.mobileSheetAuthorRow}>
                  <span className={styles.mobileSheetAuthorName}>
                    {formatCustomerName(currentReview.customer)}
                  </span>
                  <StarRating rating={currentReview.mark} size={16} />
                </div>
                <span className={styles.mobileSheetDate}>
                  {formatReviewDate(currentReview.createdAt)}
                </span>
              </div>
            </div>

            {/* Review text */}
            <p className={styles.mobileSheetText}>{currentReview.content}</p>

            {/* Thumbnails */}
            {currentReviewMedia.length > 0 && (
              <div className={styles.mobileSheetThumbnails}>
                {currentReviewMedia.map((media, index) => (
                  <button
                    key={media.id}
                    type="button"
                    onClick={() => handleThumbnailClick(media.id)}
                    className={classes([
                      styles.mobileSheetThumbnail,
                      selectedMediaIndexInReview === index &&
                        styles.mobileSheetThumbnailSelected,
                    ])}
                  >
                    {media.mediaType === 'video' ? (
                      <div className={styles.thumbnailVideoWrapper}>
                        <img
                          src={media.thumbnailUrl || media.url}
                          alt=""
                          className={styles.thumbnailImage}
                        />
                        <div className={styles.playIcon}>
                          <Icon type="play" size={14} />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={getMediaUrl(media)}
                        alt=""
                        className={styles.thumbnailImage}
                        loading="lazy"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // Desktop layout
  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Left side - Image area */}
        <div className={styles.imageSection}>
          {/* Back button */}
          <button type="button" onClick={onClose} className={styles.backButton}>
            <Icon type="chevron-3.1" size={20} />
            <span>{t('reviewBackToAllMedia')}</span>
          </button>

          {/* Navigation arrow - prev */}
          {isCarouselActive && !prevBtnDisabled && (
            <button
              type="button"
              onClick={onPrevButtonClick}
              className={classes([styles.navButton, styles.navPrev])}
            >
              <Icon type="chevron-3.1" size={24} />
            </button>
          )}

          {/* Main carousel */}
          <Carousel
            opts={{ ...opts, active: isCarouselActive }}
            setApi={setMainCarouselApi}
            className={styles.mainCarousel}
          >
            <CarouselContent className={styles.mainCarouselContent}>
              {allMediaWithReviews.map(({ media }) => (
                <CarouselItem
                  key={media.id}
                  className={styles.mainCarouselItem}
                >
                  {media.mediaType === 'video' ? (
                    <div
                      className={styles.videoWrapper}
                      onClick={() => handleVideoClick(media.id)}
                    >
                      <video
                        ref={(el) => {
                          if (el) {
                            videoRefs.current.set(media.id, el);
                          }
                        }}
                        src={getMediaUrl(media)}
                        className={styles.video}
                        controls
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  ) : (
                    <img
                      src={getMediaUrl(media)}
                      alt=""
                      className={styles.mainImage}
                      loading="lazy"
                    />
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Navigation arrow - next */}
          {isCarouselActive && !nextBtnDisabled && (
            <button
              type="button"
              onClick={onNextButtonClick}
              className={classes([styles.navButton, styles.navNext])}
            >
              <Icon type="chevron-3.1" size={24} />
            </button>
          )}
        </div>

        {/* Right side - Review panel */}
        <div className={styles.reviewPanel}>
          {/* Close button */}
          <IconButton
            icon="close"
            size={SIZE.M}
            variant="outline"
            onClick={onClose}
            className={styles.closeButton}
          />

          {/* Author section */}
          <div className={styles.authorSection}>
            <div className={styles.authorAvatar}>
              {currentReview.customer.firstName.charAt(0)}
            </div>
            <div className={styles.authorInfo}>
              <div className={styles.authorNameRow}>
                <span className={styles.authorName}>
                  {formatCustomerName(currentReview.customer)}
                </span>
                <StarRating rating={currentReview.mark} size={16} />
              </div>
              <span className={styles.reviewDate}>
                {formatReviewDate(currentReview.createdAt)}
              </span>
            </div>
          </div>

          {/* Review content */}
          <div className={styles.reviewContent}>
            <p className={styles.reviewText}>{currentReview.content}</p>
          </div>

          {/* Thumbnails grid */}
          {currentReviewMedia.length > 0 && (
            <div className={styles.thumbnailsGrid}>
              {currentReviewMedia.map((media, index) => (
                <button
                  key={media.id}
                  type="button"
                  onClick={() => handleThumbnailClick(media.id)}
                  className={classes([
                    styles.thumbnailItem,
                    selectedMediaIndexInReview === index &&
                      styles.thumbnailItemSelected,
                  ])}
                >
                  {media.mediaType === 'video' ? (
                    <div className={styles.thumbnailVideoWrapper}>
                      <img
                        src={media.thumbnailUrl || media.url}
                        alt=""
                        className={styles.thumbnailImage}
                      />
                      <div className={styles.playIcon}>
                        <Icon type="play" size={14} />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={getMediaUrl(media)}
                      alt=""
                      className={styles.thumbnailImage}
                      loading="lazy"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ReviewMediaLightbox;
